/** Presentation helpers for the onboarding invites: the status chip and the context summary. */
import { Box, Chip, Stack, Tooltip } from '@mui/material';
import type { ChipProps } from '@mui/material';

import { INVITE_SOURCE_LABELS, INVITE_STATUS_LABELS, inviteMarketplace } from '@/api/onboardingInvites';
import type { InviteContextSummary, InviteListItem, InviteSource, InviteStatus } from '@/api/onboardingInvites';
import { restechWords } from '@/features/tour-operators/onboard/restechs/registry';

const STATUS_STYLE: Record<InviteStatus, { color: ChipProps['color']; variant: ChipProps['variant'] }> = {
  available: { color: 'info', variant: 'filled' },
  claimed: { color: 'warning', variant: 'filled' },
  expired: { color: 'default', variant: 'outlined' },
  used: { color: 'success', variant: 'outlined' },
  live: { color: 'success', variant: 'filled' },
  revoked: { color: 'error', variant: 'outlined' },
};

const STATUS_HINT: Record<InviteStatus, string> = {
  available: 'Nobody has started onboarding with it yet.',
  claimed: 'A browser started onboarding and holds the link; no other browser can use it until it is released.',
  expired: 'Past its expiry: the link does not work until the expiry is extended.',
  used: 'The tenant and its admin account were created; the link is dead and the client signs in.',
  live: 'The tenant went live.',
  revoked: 'Revoked by ops: the link does not work until it is reinstated.',
};

export function inviteStatusHint(status: InviteStatus): string {
  return STATUS_HINT[status] ?? '';
}

export function InviteStatusChip({ status }: { status: InviteStatus }) {
  const style = STATUS_STYLE[status] ?? { color: 'default', variant: 'outlined' };
  return (
    <Chip
      size="small"
      color={style.color}
      variant={style.variant}
      label={INVITE_STATUS_LABELS[status] ?? status}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

const chipSx = { height: 22, fontSize: 12 } as const;

/** The grid's context column: the booking systems (or "any"), the promo code, "code edit" and
 *  how many keys nothing interprets. */
export function ContextSummaryChips({ summary }: { summary: InviteContextSummary }) {
  const uninterpreted = summary.uninterpreted ?? [];
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', overflow: 'hidden' }}>
      {summary.restechs?.length ? (
        summary.restechs.map((r) => <Chip key={r} size="small" variant="outlined" label={r} sx={chipSx} />)
      ) : (
        <Tooltip title="Every available booking system is offered" describeChild>
          <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>
            any system
          </Box>
        </Tooltip>
      )}
      {summary.promo_code && (
        <Tooltip title="Promo code" describeChild>
          <Chip size="small" variant="outlined" label={summary.promo_code} sx={{ ...chipSx, fontFamily: 'monospace' }} />
        </Tooltip>
      )}
      {summary.allow_code_edit && (
        <Tooltip title="The client may choose the tenant code" describeChild>
          <Chip size="small" variant="outlined" color="info" label="code edit" sx={chipSx} />
        </Tooltip>
      )}
      {uninterpreted.length > 0 && (
        <Tooltip title={`Not interpreted (kept as is): ${uninterpreted.join(', ')}`} describeChild>
          <Chip size="small" variant="outlined" color="warning" label={`+${uninterpreted.length} not interpreted`} sx={chipSx} />
        </Tooltip>
      )}
    </Stack>
  );
}

/** "ops@acme.example" or the user id when the e-mail is gone. */
export function userLabel(user: { id: number; email?: string | null; name?: string | null } | null | undefined): string {
  if (!user) return '—';
  return user.email || user.name || `#${user.id}`;
}

/** The words for an invite's source: "Console", …, or for one a marketplace install minted
 *  "Marketplace install · Bókun". */
export function inviteSourceLabel(invite: Pick<InviteListItem, 'source' | 'context'>): string {
  const base = INVITE_SOURCE_LABELS[invite.source as InviteSource] ?? invite.source;
  const install = invite.source === 'marketplace' ? inviteMarketplace(invite.context) : null;
  return install ? `${base} · ${restechWords(install.vendor).vendor}` : base;
}

/** The install behind a marketplace invite, for the drawer and the edit form: "Bókun account 3851
 *  (fjord)"; null for any other invite. */
export function inviteMarketplaceText(context: Record<string, unknown> | null | undefined): string | null {
  const install = inviteMarketplace(context);
  if (!install) return null;
  const account = install.account ? ` account ${install.account}` : '';
  return `${restechWords(install.vendor).vendor}${account}${install.label ? ` (${install.label})` : ''}`;
}
